import axios, { AxiosResponse } from 'axios'
import { createAsyncThunk } from '@reduxjs/toolkit'

import { AdminListResponse, AdminResponse, AdminSignInResponse, AdminType } from 'Types/admin/admin'
import { AuthUserProps } from 'Types/onboarding.types.ts'

import { ADMIN_API_ROOT, ADMIN_API_URL } from 'Constants/Global.admin.constants.ts'

import { handleError } from 'Helpers/errors.helper.ts'
import { getAccessToken, getTempAccessToken, removeTempAccessToken, setTempAccessToken } from 'Utils/auth.util.ts'
import { setUserToken } from 'Helpers/auth.helpers.ts'

/**
 * Get Admin list
 */
export const getAdminList = createAsyncThunk(
  'admin/adminList',
  async ({ deleted, pageIndex, pageSize }: { deleted?: boolean; pageIndex?: number; pageSize?: number }) => {
    let url = `${ADMIN_API_ROOT}?deleted=${deleted}`
    const queryParams = []

    if (pageIndex) {
      queryParams.push(`page=${encodeURIComponent(pageIndex)}`)
    }

    if (pageSize) {
      queryParams.push(`limit=${encodeURIComponent(pageSize)}`)
    }

    if (queryParams.length > 0) {
      const queryString = queryParams.join('&')
      url = `${url}&${queryString}`
    }

    const token = getAccessToken()
    const config = {
      headers: {
        Authorization: token,
      },
    }

    try {
      const { data }: AxiosResponse<AdminListResponse> = await axios.get(url, config)
      return {
        adminList: data.data,
        total: data.total,
      }
    } catch (error) {
      handleError(error, true)
      throw error
    }
  }
)

/**
 * Get current admin
 */
export const getAdmin = createAsyncThunk('admin/getAdmin', async () => {
  const url = `${ADMIN_API_ROOT}/profile`
  const token = getAccessToken()
  const config = {
    headers: {
      Authorization: token,
    },
  }

  try {
    const { data }: AxiosResponse<AdminResponse> = await axios.get(url, config)
    return {
      admin: data.data,
    }
  } catch (error) {
    handleError(error, true)
    throw error
  }
})

/**
 * Admin SignUp
 * @param body
 */
export const signUpAdmin = createAsyncThunk('admin/signUp', async (body: AdminType) => {
  const url = ADMIN_API_URL.AUTH.SIGNUP
  const token = getAccessToken()

  const config = {
    headers: {
      Authorization: token,
    },
  }

  try {
    const { data }: AxiosResponse<AdminResponse> = await axios.post(url, body, config)
    return data.data
  } catch (error) {
    handleError(error, true)
    throw error
  }
})

/**
 * Admin SignIn
 * @param options
 */
export const signInAdmin = async (options: AuthUserProps) => {
  const url = ADMIN_API_URL.AUTH.SIGNIN

  try {
    const { data }: AxiosResponse<AdminSignInResponse> = await axios.post(url, options)
    const { token } = data.data
    setTempAccessToken(token)
    return token
  } catch (error) {
    handleError(error, true)
    throw error
  }
}

export const validateOtp = async (otpToken: string) => {
  const url = `${ADMIN_API_URL.AUTH.VALIDATE_OTP}?otp=${otpToken}`
  const token = getTempAccessToken()

  const config = {
    headers: {
      Authorization: token,
    },
  }

  try {
    const { data }: AxiosResponse<{ data: string }> = await axios.get(url, config)
    const token: string = data.data
    removeTempAccessToken()
    setUserToken(token)
    return token
  } catch (error) {
    handleError(error, true)
    throw error
  }
}

/**
 * Update Admin
 * @param body
 */
export const updateAdmin = createAsyncThunk('admin/updateAdmin', async (body: { [key: string]: any }) => {
  const token = getAccessToken()

  const config = {
    headers: {
      Authorization: token,
    },
  }

  try {
    const { data }: AxiosResponse<AdminResponse> = await axios.patch(ADMIN_API_ROOT, body, config)
    return data.data
  } catch (error) {
    handleError(error, true)
    throw error
  }
})

/**
 * Remove admin
 * @param id
 */
export const removeAdmin = createAsyncThunk('admin/deleteAdmin', async (id: string) => {
  const url = `${ADMIN_API_ROOT}/${id}`
  const token = getAccessToken()
  const config = {
    headers: {
      Authorization: token,
    },
  }

  try {
    const { data }: AxiosResponse<AdminResponse> = await axios.delete(url, config)
    return data.data
  } catch (error) {
    handleError(error, true)
    throw error
  }
})
